/**
 * Section List Component
 * Displays list of sections with drag-and-drop reordering
 */

import { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SectionCard } from './SectionCard';
import type { PromptSection } from '@/types/promptTemplates';

interface SortableSectionProps {
  section: PromptSection;
  onEdit: (section: PromptSection) => void;
  onDelete: (sectionId: string) => void;
  onToggle: (sectionId: string, enabled: boolean) => void;
}

function SortableSection({ section, onEdit, onDelete, onToggle }: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <SectionCard
        section={section}
        isDragging={isDragging}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggle={onToggle}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

interface SectionListProps {
  sections: PromptSection[];
  onEdit: (section: PromptSection) => void;
  onDelete: (sectionId: string) => void;
  onToggle: (sectionId: string, enabled: boolean) => void;
  onReorder: (sections: PromptSection[]) => void;
}

export function SectionList({
  sections,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
}: SectionListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = sections.findIndex(s => s.id === active.id);
        const newIndex = sections.findIndex(s => s.id === over.id);

        const newSections = arrayMove(sections, oldIndex, newIndex);

        // Update order values based on new positions
        const updatedSections = newSections.map((section, index) => ({
          ...section,
          order: (index + 1) * 10, // 10, 20, 30, etc.
        }));

        onReorder(updatedSections);
      }
    },
    [sections, onReorder]
  );

  if (sections.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No sections defined. Click "Add Section" to create one.
      </div>
    );
  }

  // Sort sections by order
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedSections.map(s => s.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {sortedSections.map(section => (
            <SortableSection
              key={section.id}
              section={section}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggle={onToggle}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
